import { ComponentType, FC, useState, useMemo } from "react";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { cn } from "@src/lib/utils/cn";
import {
  TABLE_TOOLBAR_BUTTON_CLASSNAME,
  TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME,
  TABLE_TOOLBAR_LEADING_ICON_CLASSNAME,
} from "@src/lib/utils/tableToolbarChrome";
import { Badge } from "@src/lib/ui/Badge";
import { Separator } from "@src/lib/ui/Separator";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import XIcon from "@src/lib/icons/12px/x";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import CircleXFillIcon from "@src/lib/icons/18px/circle-x-fill";

export type ReportingFilterOption = {
  label: string;
  value: string;
  type: string;
};

type Props = {
  options: ReportingFilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  LeadingIcon?: ComponentType<{ className?: string }>;
};

const ReportingFilterDropdown: FC<Props> = ({
  options,
  selectedValues,
  onSelectionChange,
  placeholder = "All Items",
  searchPlaceholder = "Search...",
  noResultsText = "No items found.",
  LeadingIcon,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedOptions = useMemo(() => {
    return options.filter((option) => selectedValues.includes(option.value));
  }, [options, selectedValues]);

  const handleSelect = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newSelectedValues);
  };

  const handleRemove = (value: string) => {
    const newSelectedValues = selectedValues.filter((v) => v !== value);
    onSelectionChange(newSelectedValues);
  };

  const clearAll = () => {
    onSelectionChange([]);
    setSearchValue("");
  };

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, searchValue]);

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      clearAll();
    } else {
      onSelectionChange(options.map((opt) => opt.value));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className={cn(
            TABLE_TOOLBAR_BUTTON_CLASSNAME,
            "justify-between",
            selectedOptions.length > 0 && !LeadingIcon && "pl-1",
          )}
        >
          {LeadingIcon && (
            <LeadingIcon className={TABLE_TOOLBAR_LEADING_ICON_CLASSNAME} />
          )}
          {selectedOptions.length > 0 ? (
            <div className="flex items-center justify-between gap-1">
              <div className="flex flex-wrap items-center gap-1">
                {selectedOptions.slice(0, 3).map((option) => (
                  <Badge
                    key={option.value}
                    className="px-1.5"
                    variant="secondary"
                  >
                    {option.label}
                    <CircleXFillIcon
                      className="ml-1 size-3 cursor-pointer text-muted-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(option.value);
                      }}
                    />
                  </Badge>
                ))}
                {selectedOptions.length > 3 && (
                  <Badge variant="outline" className="px-1">
                    +{selectedOptions.length - 3} more
                  </Badge>
                )}
              </div>
              <div className="flex items-center">
                <XIcon
                  className="mx-1 size-3 cursor-pointer text-muted-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                />
                <Separator orientation="vertical" className="mr-1 h-4" />
                <ChevronDownIcon
                  className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME}
                />
              </div>
            </div>
          ) : (
            <>
              {placeholder}
              <ChevronDownIcon
                className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME}
              />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>{noResultsText}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="select-all"
                onSelect={() => toggleAll()}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded border",
                    selectedValues.length === options.length
                      ? "bg-primary border-primary"
                      : "border-muted-foreground",
                  )}
                >
                  {selectedValues.length === options.length && (
                    <CheckmarkIcon className="size-3 text-white" />
                  )}
                </div>
                <span>Select All</span>
              </CommandItem>
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 min-w-4 items-center justify-center rounded border",
                        isSelected
                          ? "bg-primary border-primary"
                          : "border-muted-foreground",
                      )}
                    >
                      {isSelected && (
                        <CheckmarkIcon className="size-3 text-white" />
                      )}
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ReportingFilterDropdown;
